/** Shared helpers for the CMS content editor and public article media (gallery + video). */

export interface GalleryItem { url: string; caption?: string; alt?: string }

const HTTPS = /^https:\/\/[^\s"'<>]+$/i;

/** Accepts legacy string galleries and captioned objects; keeps only https images; trims captions. */
export function normalizeGallery(v: unknown): GalleryItem[] {
  if (!Array.isArray(v)) return [];
  const out: GalleryItem[] = [];
  for (const g of v) {
    const raw = typeof g === "string" ? { url: g } : g && typeof g === "object" ? (g as Record<string, unknown>) : null;
    if (!raw || typeof raw.url !== "string") continue;
    const url = raw.url.trim();
    if (!HTTPS.test(url)) continue;
    const item: GalleryItem = { url };
    if (typeof raw.caption === "string" && raw.caption.trim()) item.caption = raw.caption.trim().slice(0, 300);
    if (typeof raw.alt === "string" && raw.alt.trim()) item.alt = raw.alt.trim().slice(0, 300);
    out.push(item);
  }
  return out.slice(0, 40);
}

/** YouTube/Vimeo URL → privacy-friendly embed URL; anything else returns null (shown as a link). */
export function videoEmbedUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?dnt=1`;
  return null;
}

/** Only https video links are stored. */
export function cleanVideoUrl(url: string | null | undefined): string | null {
  const v = (url ?? "").trim();
  return HTTPS.test(v) ? v : null;
}

/**
 * True when the visual editor would silently drop parts of this HTML
 * (tables, figures, non-YouTube frames, inline styles, scripts, custom wrappers…).
 * Such content opens in HTML source mode so nothing is lost.
 */
const VISUAL_TAGS = new Set(["p", "br", "strong", "b", "em", "i", "u", "s", "strike", "del", "h1", "h2", "h3", "ul", "ol", "li", "blockquote", "a", "img", "hr", "code", "pre", "span", "div"]);
export function hasLossyHtml(html: string | null | undefined): boolean {
  if (!html) return false;
  const tags = html.matchAll(/<\s*([a-z][a-z0-9-]*)\b([^>]*)>/gi);
  for (const [, name, attrs] of tags) {
    const t = name.toLowerCase();
    if (t === "iframe") { if (!/youtube(-nocookie)?\.com\/embed\//i.test(attrs)) return true; continue; }
    if (!VISUAL_TAGS.has(t)) return true;
    if (/\sstyle\s*=/i.test(attrs) && !/text-align/i.test(attrs)) return true;
    if ((t === "div" || t === "span") && /\s(class|id|data-[\w-]+)\s*=/i.test(attrs) && !/data-youtube-video/i.test(attrs)) return true;
    if (t === "h4" || t === "h5" || t === "h6") return true;
  }
  return false;
}

/** Fields whose translations a locale draft stores, with manual-edit tracking. */
export interface TranslationField { key: string; label: string; kind: "input" | "textarea" | "body" }

/** Which keys changed between the loaded translation and the edited draft. */
export function changedKeys(before: Record<string, string>, after: Record<string, string>): string[] {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((k) => (before[k] ?? "") !== (after[k] ?? ""));
}

/** Manual list after a save: previous manual keys plus edited keys, minus keys the editor unlocked. */
export function nextManual(prev: string[], edited: string[], unlocked: string[] = []): string[] {
  return [...new Set([...prev, ...edited])].filter((k) => !unlocked.includes(k)).sort();
}
